import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import Group from 'components/utils/Group'
import NumberRating from 'components/Common/NumberRating'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import styled from 'styled-components'
import { rem } from 'polished'
import {
  OfferDetailImageGallery,
  OfferDetailDescription,
  OfferDetailReviews,
  OfferDetailMap,
} from '../OfferDetail'
import type { OfferDetailReview, OfferDetailGeoLocation } from '../OfferDetail'
import type { ExperienceDetailResult, TripItineraryData } from 'tripPlanner/api/types'
import ExperienceDetailMetadata from './ExperienceDetailMetadata'
import ExperienceDetailPricing, { type TripExperienceSelection } from './ExperienceDetailPricing'
import getDrawerBookableUntilDate from './getDrawerBookableUntilDate'
import { isExperienceSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import { findOfferItemInItinerary, findOfferStopDetails } from 'tripPlanner/utils/chatItinerary'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { getDefaultTicketModes } from 'lib/experiences/ticketModeUtils'
import type { TripExperienceInitialState } from '../../hooks/useTripExperiencePageState'

const ContentWrapper = styled.div`
  margin: 0 auto;
  padding: ${rem(24)};
  width: ${rem(700)};
  maxwidth: 100%;
  box-sizing: border-box;
`

const CategoryLabel = styled(BodyText)`
  text-transform: capitalize;
`

export type DrawerExperience =
  Omit<ExperienceDetailResult, 'images' | 'redemptionLocations' | 'geoLocation'> & {
    images: Array<App.Image>
    geoLocation?: OfferDetailGeoLocation
  }

export type DrawerExperienceSearchTile = DrawerExperience & Partial<Pick<App.ExperienceOffer, 'copy' | 'giftType' | 'productType'>>

function normaliseTicketModeText(value: string | undefined): string {
  return value?.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() ?? ''
}

function ticketNameMatchesOption(ticketName: string, option: App.TicketModeField['options'][number]): boolean {
  const normalisedName = normaliseTicketModeText(ticketName)
  const normalisedText = normaliseTicketModeText(option.text)
  const normalisedKey = normaliseTicketModeText(option.key)
  return (!!normalisedText && normalisedName.includes(normalisedText)) ||
    (!!normalisedKey && normalisedName.includes(normalisedKey))
}

function deriveTicketModesMapFromTicketNames(
  ticketModes: Array<App.TicketModeField> | undefined,
  ticketNames: Array<string>,
): Record<string, string> | undefined {
  if (!ticketModes?.length || ticketNames.length === 0) return undefined

  const derived: Record<string, string> = {}
  for (const field of ticketModes) {
    const matchedOption = field.options.find(option =>
      ticketNames.some(ticketName => ticketNameMatchesOption(ticketName, option)),
    )
    const selectedKey = matchedOption?.key
    if (!selectedKey) continue

    derived[field.key] = selectedKey
    const selectedOption = field.options.find(option => option.key === selectedKey)
    Object.assign(derived, getDefaultTicketModes(selectedOption?.fields ?? []))
  }

  return Object.keys(derived).length > 0 ? derived : undefined
}

function getSavedTicketModesMap(savedData: unknown): Record<string, string> | undefined {
  if (!savedData || typeof savedData !== 'object') return undefined
  const ticketModesMap = (savedData as { ticketModesMap?: unknown }).ticketModesMap
  if (!ticketModesMap || typeof ticketModesMap !== 'object' || Array.isArray(ticketModesMap)) return undefined
  return Object.fromEntries(
    Object.entries(ticketModesMap)
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
  )
}

function getRecordKey(record: Record<string, string | number> | undefined): string {
  return Object.entries(record ?? {})
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}:${value}`)
    .join('|')
}

function getInitialPageStateKey(initialPageState: TripExperienceInitialState | undefined): string {
  if (!initialPageState) return 'none'
  return [
    initialPageState.date ?? '',
    initialPageState.time ?? '',
    initialPageState.redemptionLocationId ?? '',
    initialPageState.pickupPointId ?? '',
    getRecordKey(initialPageState.ticketCounts),
    getRecordKey(initialPageState.ticketModesMap),
  ].join(';')
}

function normaliseSavedTicketId(ticketId: string, count: number): string {
  const encodedCountPrefix = `${count}x`
  return ticketId.startsWith(encodedCountPrefix) ? ticketId.slice(encodedCountPrefix.length) : ticketId
}

interface Props {
  experience: DrawerExperience
  standaloneOffer?: App.ExperienceOffer
  reviews?: Array<OfferDetailReview>
  reviewsLoading?: boolean
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onAfterAddToTrip?: () => void
  scrollToPricing?: boolean
  checkoutSource?: App.CheckoutSource
}

function ExperienceDetailView({
  experience,
  standaloneOffer,
  reviews,
  reviewsLoading,
  itineraryData,
  onSendMessage,
  onAfterAddToTrip,
  scrollToPricing,
  checkoutSource,
}: Props) {
  const intl = useIntl()
  const {
    name,
    location,
    description,
    images,
    rating,
    reviewsTotal,
    geoLocation,
    deeplinkUrl,
    price,
    memberPrice,
    memberDiscountPercent,
    value,
    currencyCode,
    duration,
    categories,
    isSoldOut,
  } = experience

  const hasDescription = !!description
  const hasReviews = !!reviews && reviews.length > 0
  const hasGeoLocation = !!geoLocation
  const categoryLabels = categories.map((c) => c.label).join(', ')
  const bookableUntilDate = getDrawerBookableUntilDate(standaloneOffer)

  const tripItem = itineraryData ? findOfferItemInItinerary(experience.offerId, itineraryData) : undefined
  const itineraryOfferDetails = useMemo(
    () => itineraryData ? findOfferStopDetails(experience.offerId, itineraryData) : undefined,
    [experience.offerId, itineraryData],
  )
  // Prefer the specific day the experience sits on; otherwise fall back to the stop's dates
  // (start date, then its first day) so a "to be planned" item still pre-fills the calendar
  // from the itinerary rather than asking the customer to pick a date again.
  const itineraryOfferDate = itineraryOfferDetails?.dayDate
    ?? itineraryOfferDetails?.stop.startDate
    ?? itineraryOfferDetails?.stop.days?.[0]?.date
  const experienceSavedData = tripItem?.savedItemData && isExperienceSavedData(tripItem.savedItemData) ? tripItem.savedItemData : undefined
  const defaultTicketModesMap = standaloneOffer ? getDefaultTicketModes(standaloneOffer.ticketModes ?? []) : undefined
  const derivedTicketModesMap = standaloneOffer && experienceSavedData ? deriveTicketModesMapFromTicketNames(
    standaloneOffer.ticketModes,
    (experienceSavedData.tickets ?? []).map(ticket => ticket.name),
  ) : undefined
  const mergedTicketModesMap = experienceSavedData ? {
    ...defaultTicketModesMap,
    ...getSavedTicketModesMap(experienceSavedData),
    ...derivedTicketModesMap,
  } : undefined
  const initialTicketModesMap = mergedTicketModesMap && Object.keys(mergedTicketModesMap).length > 0 ? mergedTicketModesMap : undefined

  const initialPageState = useMemo(() => {
    const initialDate = experienceSavedData?.date ?? (standaloneOffer?.hasCalendar ? itineraryOfferDate : undefined)
    if (!experienceSavedData && !initialDate) return undefined

    return {
      date: initialDate,
      time: experienceSavedData?.time ?? undefined,
      ticketCounts: experienceSavedData ? Object.fromEntries(
        (experienceSavedData.tickets ?? []).map(t => [normaliseSavedTicketId(t.ticketId, t.count), t.count]),
      ) : undefined,
      redemptionLocationId: experienceSavedData?.redemptionLocationId ?? undefined,
      pickupPointId: experienceSavedData?.pickupPointId ?? undefined,
      ticketModesMap: initialTicketModesMap,
    }
  }, [experienceSavedData, initialTicketModesMap, itineraryOfferDate, standaloneOffer?.hasCalendar])
  const pricingKey = standaloneOffer ? `loaded-${getInitialPageStateKey(initialPageState)}` : 'loading'

  const handleSaveToTrip = useCallback((selection: TripExperienceSelection) => {
    if (!onSendMessage || !tripItem) return

    const totalTickets = selection.tickets.reduce((sum, t) => sum + t.count, 0)
    const ticketSummary = intl.formatMessage(
      {
        id: 'l9VadRxewhw3rkCSE0hh',
        defaultMessage: '{count, plural, one {# ticket} other {# tickets}}',
        description: 'Ticket count summary used in the save-to-trip confirmation message',
      },
      { count: totalTickets },
    )

    const dateTimeSuffix = [
      selection.date ? ` on ${moment(selection.date).format(DMY_CASUAL_FORMAT)}` : '',
      selection.time ? ` at ${selection.time}` : '',
    ].join('')

    const visible = intl.formatMessage(
      {
        id: 'jUrBvdHhRPs1mrOFU1ta',
        defaultMessage: "I've selected {tickets} for {name}{dateTime}",
        description: 'Message sent to the trip planner AI when user saves experience ticket selection',
      },
      { tickets: ticketSummary || 'tickets', name, dateTime: dateTimeSuffix },
    )

    const hidden = [
      `item_id=${tripItem.id}`,
      `experience_id=${experience.offerId}`,
      selection.date ? `date=${selection.date}` : '',
      selection.time ? `time=${selection.time}` : '',
      `tickets=${selection.tickets.map(t => `${t.count}x${t.ticketId}`).join(',')}`,
      selection.redemptionLocationId ? `redemption_location_id=${selection.redemptionLocationId}` : '',
      selection.pickupPointId ? `pickup_point_id=${selection.pickupPointId}` : '',
      selection.ticketModesMap ? `ticket_modes=${JSON.stringify(selection.ticketModesMap)}` : '',
    ].filter(Boolean).join(', ')

    onSendMessage(appendContextTag(visible, hidden, TRIP_PLANNER_ACTION_CONTEXT_LABEL))
  }, [onSendMessage, tripItem, name, experience.offerId, intl])

  return (
    <ContentWrapper>
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={12}>
          <Group direction="vertical">
            <Heading variant="heading2">{name}</Heading>
            <BodyText variant="medium" colour="light-foreground-alt">{location}</BodyText>
            {categoryLabels && (
              <CategoryLabel variant="small" colour="light-foreground-alt">{categoryLabels}</CategoryLabel>
            )}
          </Group>
          {rating !== undefined && rating > 0 && (
            <NumberRating
              rating={rating}
              total={reviewsTotal}
              variant="small"
            />
          )}
        </Group>

        <OfferDetailImageGallery images={images} />

        <ExperienceDetailMetadata
          duration={duration}
          bookableUntilDate={bookableUntilDate}
        />

        {hasDescription && (
          <>
            <Divider kind="secondary" />
            <OfferDetailDescription description={description} />
          </>
        )}

        <Divider kind="secondary" />

        <ExperienceDetailPricing
          key={pricingKey}
          price={price}
          memberPrice={memberPrice ?? undefined}
          memberDiscountPercent={memberDiscountPercent ?? undefined}
          value={value}
          currencyCode={currencyCode}
          deeplinkUrl={deeplinkUrl}
          isSoldOut={isSoldOut}
          experienceName={name}
          offerId={experience.offerId}
          coordinates={geoLocation}
          standaloneOffer={standaloneOffer}
          initialPageState={initialPageState}
          onSaveToTrip={tripItem ? handleSaveToTrip : undefined}
          itineraryData={tripItem ? undefined : itineraryData}
          onAddToTrip={tripItem ? undefined : onSendMessage}
          onAfterAddToTrip={onAfterAddToTrip}
          scrollToPricing={scrollToPricing}
          checkoutSource={checkoutSource}
        />

        {(hasReviews || reviewsLoading) && (
          <>
            <Divider kind="secondary" />
            {reviewsLoading && !hasReviews && (
              <Group direction="vertical" gap={12}>
                <LoadingBox height={20} width={120} borderRadius="S" />
                <LoadingBox height={60} width={320} borderRadius="S" />
              </Group>
            )}
            {hasReviews && (
              <OfferDetailReviews
                rating={rating}
                reviewsTotal={reviewsTotal}
                reviews={reviews}
              />
            )}
          </>
        )}

        {hasGeoLocation && (
          <>
            <Divider kind="secondary" />
            <OfferDetailMap
              geoLocation={geoLocation}
              offerName={name}
            />
          </>
        )}
      </Group>
    </ContentWrapper>
  )
}

export default ExperienceDetailView
